import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './BillingDetails.css';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';

const StaffList = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [newStaff, setNewStaff] = useState({ name: '', email: '', password: '', role: 'Staff' });
  const [message, setMessage] = useState('');
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [editStaff, setEditStaff] = useState(null);
  const [currentlyPresentStaff, setCurrentlyPresentStaff] = useState([]);
  const [isLoadingPresentStaff, setIsLoadingPresentStaff] = useState(false);
  const { currentUser } = useAuth();
  
  const API_BASE_URL = API_CONFIG.BASE_URL;
  
  // Get Firebase ID token for API calls
  const getAuthToken = useCallback(async () => {
    if (!currentUser) {
      return 'mock-token-for-testing';
    }
    
    // Check if we've already failed Firebase auth recently
    const lastFirebaseError = localStorage.getItem('firebaseAuthFailed');
    const errorTime = lastFirebaseError ? parseInt(lastFirebaseError) : 0;
    const now = Date.now();
    
    // If Firebase failed in the last 10 minutes, skip trying again
    if (now - errorTime < 600000) { // 10 minutes
      return 'mock-token-for-testing';
    }
    
    try {
      return await currentUser.getIdToken(false); // Use false to avoid forcing refresh
    } catch (error) {
      // Store the failure time to avoid repeated attempts
      localStorage.setItem('firebaseAuthFailed', now.toString());
      return 'mock-token-for-testing';
    }
  }, [currentUser]);
  
  const api = axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

useEffect(() => {
  const fetchStaff = async () => {
    if (!currentUser) return;
    
    setIsLoading(true);
    try {
      const token = await getAuthToken();
      const response = await api.get('/accounts/staff/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data);
    } catch (err) {
      setNotificationMessage({ type: 'error', text: `Failed to fetch staff list: ${err.message}` });
      setTimeout(() => setNotificationMessage(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };
  fetchStaff();
}, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

// Fetch staff details from attendance collection (role = staff only)
useEffect(() => {
  const fetchStaffFromAttendance = async () => {
    if (!currentUser) return;
    
    setIsLoadingPresentStaff(true);
    try {
      const token = await getAuthToken();
      const response = await api.get('/attendance/staff-details', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentlyPresentStaff(response.data?.staff || []);
      
      // Show message if no staff are logged in
      if (response.data?.message && response.data.message.includes('No staffs available')) {
        setNotificationMessage({ 
          type: 'info', 
          text: 'No staffs available - no one is currently logged in as staff' 
        });
        setTimeout(() => setNotificationMessage(null), 5000);
      }
    } catch (err) {
      setCurrentlyPresentStaff([]);
      setNotificationMessage({ type: 'error', text: `Failed to fetch staff details: ${err.message}` });
      setTimeout(() => setNotificationMessage(null), 3000);
    } finally {
      setIsLoadingPresentStaff(false);
    }
  };
  
  fetchStaffFromAttendance();
}, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleAddStaff = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const token = await getAuthToken();
      const response = await api.post('/auth/staff/add', newStaff, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff([...staff, response.data]);
      setNewStaff({ name: '', email: '', password: '', role: 'Staff' });
      setNotificationMessage({ type: 'success', text: 'Staff added successfully.' });
    } catch (err) {
      setNotificationMessage({ type: 'error', text: `Failed to add staff: ${err.message}` });
    } finally {
      setIsLoading(false);
      setTimeout(() => setNotificationMessage(null), 3000);
    }
  };

  const handleEditStaff = async (e) => {
    e.preventDefault();
    if (!editStaff) return;
    setIsLoading(true);
    try {
      const token = await getAuthToken();
      const response = await api.put(`/auth/staff/${editStaff._id}`, editStaff, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(staff.map(s => s._id === editStaff._id ? response.data : s));
      setEditStaff(null);
      setNotificationMessage({ type: 'success', text: 'Staff updated successfully.' });
    } catch (err) {
      setNotificationMessage({ type: 'error', text: `Failed to update staff: ${err.message}` });
    } finally {
      setIsLoading(false);
      setTimeout(() => setNotificationMessage(null), 3000);
    }
  };

  const handleSelectStaff = (s) => {
    setEditStaff({ ...s, password: '' }); // Reset password field for editing
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!selectedStaffIds.length || !message) {
      setNotificationMessage({ type: 'error', text: 'Select staff and enter a message.' });
      setTimeout(() => setNotificationMessage(null), 3000);
      return;
    }
    setIsLoading(true);
    try {
      const token = await getAuthToken();
      // Send message to each selected staff member individually
      const messagePromises = selectedStaffIds.map(async (staffId) => {
        const messageData = {
          staffId: staffId,
          message: message,
          sender: 'Accounts Admin',
          senderEmail: 'accounts@admin.com',
          senderRole: 'Accounts Admin',
          type: 'notification',
          isRead: false,
          createdAt: new Date().toISOString(),
          priority: 'normal'
        };

        const response = await fetch(`${API_BASE_URL}/api/marketing/send-message`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(messageData)
        });

        if (!response.ok) {
          throw new Error(`Failed to send message to staff ${staffId}`);
        }

        return response.json();
      });

      await Promise.all(messagePromises);
      setMessage('');
      setSelectedStaffIds([]);
      setNotificationMessage({ type: 'success', text: 'Message sent successfully.' });
    } catch (err) {
      setNotificationMessage({ type: 'error', text: `Failed to send message: ${err.message}` });
    } finally {
      setIsLoading(false);
      setTimeout(() => setNotificationMessage(null), 3000);
    }
  };

  const toggleStaffSelection = (staffId) => {
    setSelectedStaffIds((prev) =>
      prev.includes(staffId) ? prev.filter((id) => id !== staffId) : [...prev, staffId]
    );
  };

  return (
    <div className="staff-list-container">
      <div className="staff-list-header">
        <button 
          onClick={() => navigate(-1)} 
          className="back-button"
          aria-label="Go back"
        >
          ← Back
        </button>
        <h2>Staff Management</h2>
        {notificationMessage && (
          <div className={`notification-message ${notificationMessage.type}`}>
            <p>{notificationMessage.text}</p>
            <button
              type="button"
              onClick={() => setNotificationMessage(null)}
              aria-label="Close message"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {isLoading && <div className="loading-overlay">Loading...</div>}

      <div className="staff-list-content">
        {/* Currently Logged In Staff Section */}
        <div className="logged-in-staff-section">
          <div className="section-header">
            <h3>🟢 Currently Logged In Staff</h3>
            <div className="header-controls">
              <div className="staff-count-badge">
                {isLoadingPresentStaff ? (
                  <span className="loading-text">Loading...</span>
                ) : (
                  <span className="count">{currentlyPresentStaff.length}</span>
                )}
              </div>
              <button 
                onClick={() => {
                  setIsLoadingPresentStaff(true);
                  const fetchStaffFromAttendance = async () => {
                    if (!currentUser) return;
                    
                    try {
                      const token = await getAuthToken();
                      const response = await api.get('/attendance/staff-details', {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setCurrentlyPresentStaff(response.data?.staff || []);
                      
                      if (response.data?.message && response.data.message.includes('No staffs available')) {
                        setNotificationMessage({ 
                          type: 'info', 
                          text: 'No staffs available - no one is currently logged in as staff' 
                        });
                        setTimeout(() => setNotificationMessage(null), 5000);
                      }
                    } catch (err) {
                      setCurrentlyPresentStaff([]);
                      setNotificationMessage({ type: 'error', text: `Failed to fetch staff details: ${err.message}` });
                      setTimeout(() => setNotificationMessage(null), 3000);
                    } finally {
                      setIsLoadingPresentStaff(false);
                    }
                  };
                  fetchStaffFromAttendance();
                }}
                className="refresh-button"
                disabled={isLoadingPresentStaff}
                aria-label="Refresh staff list"
              >
                {isLoadingPresentStaff ? 'Refreshing...' : '🔄 Refresh'}
              </button>
            </div>
          </div>
          
          {isLoadingPresentStaff ? (
            <div className="loading-staff">
              <div className="loading-spinner"></div>
              <p>Loading logged in staff...</p>
            </div>
          ) : currentlyPresentStaff.length > 0 ? (
            <div className="active-staff-grid">
              {currentlyPresentStaff.map((staff, index) => (
                <div key={staff.id || staff._id || index} className="active-staff-card">
                  <div className="staff-avatar">
                    {staff.profilePhoto ? (
                      <img 
                        src={staff.profilePhoto} 
                        alt={staff.name} 
                        className="avatar-image"
                      />
                    ) : (
                      <span className="avatar-initial">
                        {staff.name ? staff.name.charAt(0).toUpperCase() : '?'}
                      </span>
                    )}
                  </div>
                  <div className="staff-info">
                    <h4 className="staff-name">{staff.name || 'Unknown Staff'}</h4>
                    <p className="staff-email">{staff.email}</p>
                    <p className="staff-role">{staff.role}</p>
                    {staff.department && <p className="staff-department">{staff.department}</p>}
                    <p className="login-time">
                      <strong>Logged in:</strong> {staff.loginTime || 'N/A'}
                    </p>
                    <p className="session-duration">
                      <strong>Active for:</strong> {staff.sessionDuration || 'N/A'}
                    </p>
                    <p className="attendance-status">
                      <strong>Status:</strong> <span className="status-present">Logged In</span>
                    </p>
                  </div>
                  <div className="status-indicator">
                    <div className="pulse-dot"></div>
                    <span className="status-text">Online</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-active-staff">
              <div className="no-staff-icon">😴</div>
              <h4>No Staffs Available</h4>
              <p>No one is currently logged in as staff.</p>
            </div>
          )}
        </div>

        {/* Staff Management Section */}
        <div className="staff-management-section">
          <div className="management-forms">
            <div className="form-section">
              <h3>Add Staff</h3>
              <form onSubmit={handleAddStaff} className="add-staff-form">
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Name"
                    value={newStaff.name}
                    onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                    required
                    aria-label="Staff name"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={newStaff.email}
                    onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                    required
                    aria-label="Staff email"
                  />
                </div>
                <div className="form-row">
                  <input
                    type="password"
                    placeholder="Password"
                    value={newStaff.password}
                    onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                    required
                    aria-label="Staff password"
                    autoComplete="new-password"
                  />
                  <select
                    value={newStaff.role}
                    onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}
                    disabled
                    aria-label="Staff role"
                  >
                    <option value="Staff">Staff</option>
                  </select>
                </div>
                <button type="submit" className="submit-btn" aria-label="Add staff">Add Staff</button>
              </form>
            </div>

            {editStaff && (
              <div className="form-section">
                <h3>Edit Staff</h3>
                <form onSubmit={handleEditStaff} className="add-staff-form">
                  <div className="form-row">
                    <input
                      type="text"
                      placeholder="Name"
                      value={editStaff.name}
                      onChange={(e) => setEditStaff({ ...editStaff, name: e.target.value })}
                      required
                      aria-label="Edit staff name"
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      value={editStaff.email}
                      onChange={(e) => setEditStaff({ ...editStaff, email: e.target.value })}
                      required
                      aria-label="Edit staff email"
                    />
                  </div>
                  <div className="form-row">
                    <input
                      type="password"
                      placeholder="New Password (optional)"
                      value={editStaff.password || ''}
                      onChange={(e) => setEditStaff({ ...editStaff, password: e.target.value })}
                      aria-label="Edit staff password"
                      autoComplete="new-password"
                    />
                    <select
                      value={editStaff.role}
                      onChange={(e) => setEditStaff({ ...editStaff, role: e.target.value })}
                      disabled
                      aria-label="Edit staff role"
                    >
                      <option value="Staff">Staff</option>
                    </select>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="save-btn" aria-label="Save changes">Save</button>
                    <button type="button" onClick={() => setEditStaff(null)} className="cancel-btn" aria-label="Cancel edit">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            <div className="form-section">
              <h3>Send Message</h3>
              <form onSubmit={handleSendMessage} className="send-message-form">
                <textarea
                  placeholder="Enter message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  aria-label="Message content"
                ></textarea>
                <button type="submit" className="send-btn" aria-label="Send message">Send Message</button>
              </form>
            </div>
          </div>
        </div>

        {/* All Staff Members Table - Moved to Bottom */}
        <div className="staff-table-section">
          <h3>All Staff Members</h3>
          <table className="staff-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {staff.length > 0 ? (
                staff.map((s) => (
                  <tr key={s._id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedStaffIds.includes(s._id)}
                        onChange={() => toggleStaffSelection(s._id)}
                        aria-label={`Select ${s.name}`}
                      />
                    </td>
                    <td>{s.name}</td>
                    <td>{s.email}</td>
                    <td>{s.role}</td>
                    <td>
                      <button onClick={() => handleSelectStaff(s)} className="edit-btn" aria-label={`Edit ${s.name}`}>Edit</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="no-data">No staff available.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StaffList;